import React, { useCallback, useEffect, useRef, useState } from 'react'
import { IconChevronLeft, IconChevronRight } from '@tabler/icons-react'

// Custom hook for horizontal scroll with arrows and custom scrollbar
export const useCustomScrollbar = (items: any[]) => {
  const [scrollState, setScrollState] = useState({
    showLeft: false,
    showRight: false,
    progress: 0,
    thumbWidth: 100
  })
  const [isHovering, setIsHovering] = useState(false)
  const [isDragging, setIsDragging] = useState(false)
  const scrollRef = useRef<HTMLDivElement>(null)
  const scrollbarRef = useRef<HTMLDivElement>(null)
  const dragStartXRef = useRef(0)
  const dragStartScrollRef = useRef(0)

  const updateScrollState = useCallback((container: HTMLDivElement) => {
    const { scrollLeft, scrollWidth, clientWidth } = container
    const maxScroll = scrollWidth - clientWidth
    if (maxScroll > 0) {
      setScrollState({
        progress: (scrollLeft / maxScroll) * 100,
        thumbWidth: Math.max((clientWidth / scrollWidth) * 100, 10),
        showLeft: scrollLeft > 0,
        showRight: scrollLeft < maxScroll - 1
      })
    } else {
      setScrollState({ progress: 0, thumbWidth: 100, showLeft: false, showRight: false })
    }
  }, [])

  // Enhanced smooth scroll with visible animation
  const smoothScrollTo = useCallback((delta: number) => {
    const container = scrollRef.current
    if (!container) return

    const start = container.scrollLeft
    const target = start + delta
    const duration = 600 // 600ms for more visible animation
    const startTime = performance.now()

    // Easing function for smooth deceleration
    const easeOutCubic = (t: number) => 1 - Math.pow(1 - t, 3)

    const animateScroll = (currentTime: number) => {
      const elapsed = currentTime - startTime
      const progress = Math.min(elapsed / duration, 1)
      const eased = easeOutCubic(progress)

      container.scrollLeft = start + (target - start) * eased

      if (progress < 1) {
        requestAnimationFrame(animateScroll)
      }
    }

    requestAnimationFrame(animateScroll)
  }, [])

  useEffect(() => {
    if (!isDragging) return

    const handleMouseMove = (e: MouseEvent) => {
      const container = scrollRef.current
      const scrollbar = scrollbarRef.current
      if (!container || !scrollbar) return

      // Direct 1:1 pixel mapping for smooth drag
      const deltaX = e.clientX - dragStartXRef.current
      const trackWidth = scrollbar.clientWidth
      const thumbWidthPx = (scrollState.thumbWidth / 100) * trackWidth
      const availableSpace = trackWidth - thumbWidthPx

      if (availableSpace <= 0) return

      // Calculate scroll position with 1:1 thumb movement
      const scrollableWidth = container.scrollWidth - container.clientWidth
      const scrollPerPixel = scrollableWidth / availableSpace
      const newScrollLeft = dragStartScrollRef.current + deltaX * scrollPerPixel

      container.scrollLeft = Math.max(0, Math.min(scrollableWidth, newScrollLeft))

      // Update state immediately without waiting for scroll event
      const progress = scrollableWidth > 0 ? (container.scrollLeft / scrollableWidth) * 100 : 0
      setScrollState((prev) => ({ ...prev, progress }))
    }

    const handleMouseUp = () => setIsDragging(false)

    document.addEventListener('mousemove', handleMouseMove, { passive: false })
    document.addEventListener('mouseup', handleMouseUp)
    document.body.style.cursor = 'grabbing'
    document.body.style.userSelect = 'none'

    return () => {
      document.removeEventListener('mousemove', handleMouseMove)
      document.removeEventListener('mouseup', handleMouseUp)
      document.body.style.cursor = ''
      document.body.style.userSelect = ''
    }
  }, [isDragging, scrollState.thumbWidth])

  useEffect(() => {
    const container = scrollRef.current
    if (container && items?.length > 0) {
      updateScrollState(container)
    }
  }, [items, updateScrollState])

  return {
    scrollState,
    isHovering,
    setIsHovering,
    isDragging,
    setIsDragging,
    scrollRef,
    scrollbarRef,
    dragStartXRef,
    dragStartScrollRef,
    updateScrollState,
    smoothScrollTo
  }
}

// Reusable scroll container component
interface ScrollContainerProps {
  scrollHook: ReturnType<typeof useCustomScrollbar>
  children: React.ReactNode
}

export const ScrollContainer: React.FC<ScrollContainerProps> = ({ scrollHook, children }) => {
  const {
    scrollState,
    isHovering,
    setIsHovering,
    isDragging,
    setIsDragging,
    scrollRef,
    scrollbarRef,
    dragStartXRef,
    dragStartScrollRef,
    updateScrollState,
    smoothScrollTo
  } = scrollHook

  // Only show scrollbar if there's content to scroll (thumbWidth < 99%)
  const hasScrollableContent = scrollState.thumbWidth < 99

  return (
    <div
      className="relative"
      onMouseEnter={() => {
        setIsHovering(true)
        if (scrollRef.current) updateScrollState(scrollRef.current)
      }}
      onMouseLeave={() => setIsHovering(false)}>
      {/* Left Arrow */}
      {scrollState.showLeft && isHovering && hasScrollableContent && (
        <button
          className="scroll-arrow absolute left-2 top-[40%] -translate-y-1/2 z-10 shadow-lg rounded-full p-1"
          // Scroll delta of 400px provides optimal UX - cards have min-width ~350px,
          onClick={() => smoothScrollTo(-400)}>
          <IconChevronLeft size={24} color="white" />
        </button>
      )}

      {/* Left overlay to block interactions under arrow */}
      {scrollState.showLeft && isHovering && hasScrollableContent && (
        <div
          className="scroll-overlay-left absolute left-0 top-0 bottom-0 w-[80px] z-[9] pointer-events-auto cursor-default"
          onClick={(e) => e.preventDefault()}
        />
      )}

      {/* Scrollable Container */}
      <div
        ref={scrollRef}
        className="flex overflow-x-auto mt-0 scroll-smooth snap-none"
        onScroll={(e) => {
          if (!isDragging) updateScrollState(e.currentTarget)
        }}>
        {children}
      </div>

      {/* Right overlay to block interactions under arrow */}
      {scrollState.showRight && isHovering && hasScrollableContent && (
        <div
          className="scroll-overlay-right absolute right-0 top-0 bottom-0 w-[80px] z-[9] pointer-events-auto cursor-default"
          onClick={(e) => e.preventDefault()}
        />
      )}

      {/* Right Arrow */}
      {scrollState.showRight && isHovering && hasScrollableContent && (
        <button
          className="scroll-arrow absolute right-2 top-[40%] -translate-y-1/2 z-10 shadow-lg rounded-full p-1"
          // Scroll delta of 400px provides optimal UX - cards have min-width ~350px,
          onClick={() => smoothScrollTo(400)}>
          <IconChevronRight size={24} color="white" />
        </button>
      )}

      {/* Custom Progress Bar Scrollbar - Always visible with varying opacity */}
      {hasScrollableContent && (
        <div
          ref={scrollbarRef}
          className="mx-auto"
          style={{
            width: '300px',
            height: '6px',
            backgroundColor: 'var(--theme-border)',
            borderRadius: '10px',
            position: 'relative',
            cursor: 'pointer',
            opacity: 1,
            transition: 'opacity 0.2s ease'
          }}
          onClick={(e) => {
            if (isDragging || !scrollRef.current || !scrollbarRef.current) return
            const rect = e.currentTarget.getBoundingClientRect()
            const clickX = e.clientX - rect.left
            const trackWidth = rect.width
            const thumbWidthPx = (scrollState.thumbWidth / 100) * trackWidth
            const availableSpace = trackWidth - thumbWidthPx

            // Position thumb so its center aligns with click point
            const targetThumbLeft = Math.max(0, Math.min(availableSpace, clickX - thumbWidthPx / 2))
            const scrollPercentage = availableSpace > 0 ? targetThumbLeft / availableSpace : 0
            const scrollableWidth = scrollRef.current.scrollWidth - scrollRef.current.clientWidth

            scrollRef.current.scrollTo({
              left: scrollPercentage * scrollableWidth,
              behavior: 'smooth'
            })
          }}>
          <div
            style={{
              width: `${scrollState.thumbWidth}%`,
              height: '100%',
              backgroundColor: 'var(--brand-accent-color)',
              borderRadius: '10px',
              transition: isDragging ? 'none' : 'left 0.1s ease',
              position: 'absolute',
              left: `${(scrollState.progress * (100 - scrollState.thumbWidth)) / 100}%`,
              cursor: isDragging ? 'grabbing' : 'grab'
            }}
            onMouseDown={(e) => {
              e.preventDefault()
              e.stopPropagation()
              const container = scrollRef.current
              if (container) {
                dragStartXRef.current = e.clientX
                dragStartScrollRef.current = container.scrollLeft
                setIsDragging(true)
              }
            }}
          />
        </div>
      )}

      <style>{`
        .flex.overflow-x-auto {
          scrollbar-width: none;
          -ms-overflow-style: none;
          scroll-behavior: smooth;
        }
        .flex.overflow-x-auto::-webkit-scrollbar {
          display: none;
        }
        .scroll-arrow {
          background-color: var(--color-primary);
          border: none;
          cursor: pointer;
          transition: all 0.2s ease;
        }
        .scroll-arrow:hover {
          opacity: 0.9;
          transform: scale(1.05);
        }
        .scroll-arrow:active {
          transform: scale(0.95);
        }
        .scroll-overlay-left {
          background: linear-gradient(to right, var(--surface-bg), transparent);
        }
        .scroll-overlay-right {
          background: linear-gradient(to left, var(--surface-bg), transparent);
        }
      `}</style>
    </div>
  )
}
